import { useState, useEffect, useCallback, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useMutation, useQuery } from "@apollo/client"
import {
  ADMIN_CUSTOMER_INQUIRIES,
  ADMIN_CUSTOMER_INQUIRY,
  ADMIN_ADD_CUSTOMER_INQUIRY_REPLY,
  ADMIN_UPDATE_CUSTOMER_INQUIRY_STATUS,
} from "../../../graphql/customerInquiry"
import { mainClient } from "../../../../apollo"
import { toast } from "sonner"
import { useSubscriptionEvent } from "../../../../utils/subscriptionEvents"
import { useRealTimeDispatch } from "../../reducer/RealTimeContext"

const PAGE_SIZE = 50

export const INQUIRY_STATUS = {
  PENDING: 0,
  IN_PROGRESS: 1,
  RESOLVED: 2,
  CANCELLED: 3,
} as const

export const INQUIRY_STATUS_LABEL: Record<number, string> = {
  [INQUIRY_STATUS.PENDING]: "대기중",
  [INQUIRY_STATUS.IN_PROGRESS]: "처리중",
  [INQUIRY_STATUS.RESOLVED]: "완료",
  [INQUIRY_STATUS.CANCELLED]: "취소됨",
}

export interface InquiryUser {
  id: string
  userName: string
  email: string
  phoneNo: string
}

export interface InquiryReply {
  id: number
  content: string
  isAdminReply: boolean
  authorName: string
  createdAt: string
}

export interface InquiryRecord {
  id: number
  title: string
  content: string
  status: number
  createdAt: string
  updatedAt: string
  replyCount: number
  user: InquiryUser
  replies?: InquiryReply[]
}

export function useCustomerInquiry() {
  const realTimeDispatch = useRealTimeDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  // mode 0: 목록, 1: 상세, 2: 답변작성
  const [mode, setMode] = useState(0)
  const [statusFilter, setStatusFilter] = useState<number | null>(null)
  const [dataSource, setDataSource] = useState<InquiryRecord[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [selectedInquiry, setSelectedInquiry] = useState<InquiryRecord | null>(null)
  const [replyContent, setReplyContent] = useState("")
  const [listLoading, setListLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const pageOffsetRef = useRef(0)

  const hasMore = dataSource.length > 0 && dataSource.length < totalCount

  // 목록 로드
  const fetchList = useCallback(async () => {
    setListLoading(true)
    try {
      const { data, error } = await mainClient.query({
        query: ADMIN_CUSTOMER_INQUIRIES,
        fetchPolicy: "no-cache",
        variables: {
          skip: 0,
          take: PAGE_SIZE,
          filter: statusFilter !== null ? { status: statusFilter } : undefined,
        },
      })

      if (error) {
        toast.error(`목록 조회 실패: ${error}`)
        return
      }

      const result = data?.adminInquiries
      if (result?.data) {
        setDataSource(Array.isArray(result.data) ? result.data : [])
        setTotalCount(result.totalCount ?? 0)
        pageOffsetRef.current = PAGE_SIZE
      }
    } catch (err: any) {
      toast.error(`문의 목록을 불러오는데 실패했습니다: ${err.message}`)
    } finally {
      setListLoading(false)
    }
  }, [statusFilter])

  const loadMore = useCallback(async () => {
    if (isLoadingMore) return
    setIsLoadingMore(true)
    try {
      const skip = pageOffsetRef.current
      const { data } = await mainClient.query({
        query: ADMIN_CUSTOMER_INQUIRIES,
        fetchPolicy: "no-cache",
        variables: {
          skip,
          take: PAGE_SIZE,
          filter: statusFilter !== null ? { status: statusFilter } : undefined,
        },
      })

      const result = data?.adminInquiries
      const newRows = result?.data || []
      pageOffsetRef.current = skip + PAGE_SIZE
      setDataSource((prev) => [...prev, ...newRows])
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, statusFilter])

  useEffect(() => {
    if (mode === 0) {
      fetchList()
    }
  }, [statusFilter, mode, fetchList])

  // 새 문의 실시간 갱신
  useSubscriptionEvent("newInquiry", (inquiry: any) => {
    if (mode === 0) {
      fetchList()
    } else if (selectedId && inquiry?.id === selectedId) {
      // 상세 보기 중인 문의에 유저가 답변을 달면 즉시 갱신
      refetchInquiry()
    }
  })

  // 우측 패널 클릭 → 해당 문의 상세로 바로 진입
  useEffect(() => {
    const state = location.state as { inquiryId?: number } | null
    if (state?.inquiryId) {
      setSelectedId(state.inquiryId)
      setMode(1)
      // state 소비 후 제거 (뒤로가기 시 재진입 방지)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state])

  // 상세 조회
  const { refetch: refetchInquiry, loading: detailLoading } = useQuery(
    ADMIN_CUSTOMER_INQUIRY,
    {
      variables: { id: selectedId },
      client: mainClient,
      skip: !selectedId,
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        setSelectedInquiry(data.adminInquiry)
      },
      onError: (error) => {
        toast.error("문의 상세 정보를 불러오는데 실패했습니다.")
        console.error("상세 조회 실패:", error)
      },
    }
  )

  // 답변 mutation
  const [addReply, { loading: addReplyLoading }] = useMutation(
    ADMIN_ADD_CUSTOMER_INQUIRY_REPLY,
    {
      client: mainClient,
      onCompleted: ({ adminAddReply }) => {
        if (adminAddReply.success) {
          toast.success("답변이 등록되었습니다.")
          setReplyContent("")
          setMode(1)
          refetchInquiry()
          fetchList()
          // 우측 실시간 요청 패널에서 해당 문의 제거
          if (selectedId !== null) {
            realTimeDispatch({ type: "DISMISS_INQUIRY", id: selectedId })
          }
        } else {
          toast.error(adminAddReply.message || "답변 등록에 실패했습니다.")
        }
      },
      onError: () => toast.error("답변 등록에 실패했습니다."),
    }
  )

  // 상태 변경 mutation
  const [updateStatus, { loading: updateStatusLoading }] = useMutation(
    ADMIN_UPDATE_CUSTOMER_INQUIRY_STATUS,
    {
      client: mainClient,
      onCompleted: ({ adminUpdateStatus }) => {
        if (adminUpdateStatus.success) {
          toast.success("상태가 변경되었습니다.")
          refetchInquiry()
          fetchList()
          // 완료(2) 또는 취소(3)로 변경 시 우측 패널에서 제거
          const newStatus = adminUpdateStatus.inquiry?.status
          if ((newStatus === 2 || newStatus === 3) && selectedId !== null) {
            realTimeDispatch({ type: "DISMISS_INQUIRY", id: selectedId })
          }
        } else {
          toast.error(adminUpdateStatus.message || "상태 변경에 실패했습니다.")
        }
      },
      onError: () => toast.error("상태 변경에 실패했습니다."),
    }
  )

  const handleSelectInquiry = useCallback((id: number) => {
    setSelectedId(id)
    setMode(1)
  }, [])

  const handleBackToList = useCallback(() => {
    setMode(0)
    setSelectedId(null)
    setSelectedInquiry(null)
    setReplyContent("")
  }, [])

  const handleSubmitReply = useCallback(() => {
    if (!replyContent.trim()) {
      toast.warning("답변 내용을 입력해주세요.")
      return
    }
    addReply({
      variables: { inquiryId: selectedId, content: replyContent },
    })
  }, [replyContent, selectedId, addReply])

  const handleStatusChange = useCallback(
    (newStatus: number) => {
      updateStatus({ variables: { id: selectedId, status: newStatus } })
    },
    [selectedId, updateStatus]
  )

  return {
    mode,
    setMode,
    statusFilter,
    setStatusFilter,
    dataSource,
    totalCount,
    listLoading,
    selectedInquiry,
    detailLoading: detailLoading || updateStatusLoading,
    replyContent,
    setReplyContent,
    addReplyLoading,
    handleSelectInquiry,
    handleBackToList,
    handleSubmitReply,
    handleStatusChange,
    loadMore,
    hasMore,
    isLoadingMore,
  }
}
